// ─────────────────── image (photos → PDF) import view ───────────────────
//
// Full-screen import flow (ImportMdView's pattern): pick a layout
// theme, tune the grid / fit / frame / captions, reorder or drop
// images, watch the real preview — every page shown is a render of the
// ACTUAL generated PDF, so confirming locks in exactly what's on
// screen. Rebuilds are debounced engine round-trips (files were
// decoded once, in the route's picker); the worker keeps the generated
// document as scratch state until commit/cancel.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CustomFont } from "../lib/fonts.ts";
import type { MdFace, PageInfo, RenderResult } from "../lib/protocol.ts";
import type { ResolvedImportImage } from "../lib/image-import.ts";
import type { PhotoItem } from "../lib/photo-layout.ts";
import {
  PHOTO_THEMES,
  clonePhotoTheme,
  photoThemeFamilies,
  type PhotoThemeConfig,
  type PhotoThemePreset,
} from "../lib/photo-theme.ts";
import type { SheetLayoutMeta } from "../lib/sheet-layout.ts";
import { MM_TO_PT, PAGE_PRESETS } from "../lib/ui-constants.ts";
import { ColorPicker } from "./ColorPicker.tsx";
import {
  AlignSelect,
  FontSelect,
  Num,
  PreviewPage,
  Row,
  Section,
  fieldCls,
  resolveFaces,
} from "./import-shared.tsx";

const THEME_STORE_KEY = "pdfEditorPhotoImportTheme";

// ───────────────────────── stored theme ─────────────────────────

interface ThemeChoice {
  presetId: string;
  config: PhotoThemeConfig;
  dirty: boolean;
}

function defaultChoice(): ThemeChoice {
  const preset = PHOTO_THEMES[0]!;
  return { presetId: preset.id, config: clonePhotoTheme(preset.config), dirty: false };
}

function loadStoredChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(THEME_STORE_KEY);
    if (!raw) return defaultChoice();
    const parsed = JSON.parse(raw) as Partial<ThemeChoice>;
    const c = parsed.config;
    if (
      !c ||
      typeof c !== "object" ||
      !c.page ||
      !c.layout ||
      !c.frame ||
      !c.caption ||
      !c.pageHeader ||
      !c.pageFooter
    ) {
      return defaultChoice();
    }
    return {
      presetId: typeof parsed.presetId === "string" ? parsed.presetId : "full",
      config: c as PhotoThemeConfig,
      dirty: parsed.dirty === true,
    };
  } catch {
    return defaultChoice();
  }
}

// ───────────────────────── theme card ─────────────────────────

/** Theme gallery card — a CSS caricature of the preset's grid. */
function ThemeCard({
  preset,
  active,
  onPick,
}: {
  preset: PhotoThemePreset;
  active: boolean;
  onPick: () => void;
}) {
  const c = preset.config;
  const cells = Math.min(12, c.layout.columns * c.layout.rows);
  return (
    <button
      type="button"
      onClick={onPick}
      title={preset.blurb}
      className={`flex flex-col gap-1 rounded-md border p-1.5 text-left ${
        active
          ? "border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]"
          : "border-[color:var(--color-border)] hover:border-[color:var(--color-border-strong)]"
      }`}
    >
      <div
        className="grid h-20 w-full gap-[3px] overflow-hidden rounded-sm border border-[color:var(--color-border)]"
        style={{
          background: c.page.background,
          padding: c.page.marginLeft === 0 ? 0 : 4,
          gridTemplateColumns: `repeat(${c.layout.columns}, 1fr)`,
          gridTemplateRows: `repeat(${c.layout.rows}, 1fr)`,
        }}
      >
        {Array.from({ length: cells }, (_, i) => (
          <div
            key={i}
            className="flex min-h-0 flex-col items-center justify-center"
            style={{
              background: c.frame.matWidth > 0 ? c.frame.matColor : "transparent",
              border: c.frame.borderWidth > 0 ? `1px solid ${c.frame.borderColor}` : "none",
              padding: c.frame.matWidth > 0 ? 2 : 0,
            }}
          >
            <span
              className="block w-full flex-1"
              style={{ background: i % 2 === 0 ? "#9ca3af" : "#6b7280", minHeight: 4 }}
            />
            {c.caption.source && (
              <span
                className="mt-[2px] block h-[2px] w-2/3 rounded-xs"
                style={{ background: c.caption.color, opacity: 0.6 }}
              />
            )}
          </div>
        ))}
      </div>
      <span className="text-[11px] font-medium leading-tight">{preset.label}</span>
    </button>
  );
}

// ───────────────────────── image strip ─────────────────────────

/** Reorderable thumbnail strip — HTML5 dnd, same idiom as the page
 *  thumbnail rail. */
function ImageStrip({
  images,
  onReorder,
  onRemove,
}: {
  images: ResolvedImportImage[];
  onReorder: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  return (
    <div className="grid grid-cols-3 gap-1.5">
      {images.map((img, i) => (
        <div
          key={img.id}
          draggable
          onDragStart={() => setDragIndex(i)}
          onDragEnd={() => {
            setDragIndex(null);
            setOverIndex(null);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIndex(i);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragIndex !== null && dragIndex !== i) onReorder(dragIndex, i);
            setDragIndex(null);
            setOverIndex(null);
          }}
          title={`${img.name} — ${img.width}×${img.height}`}
          className={`group relative aspect-square cursor-grab overflow-hidden rounded-sm border ${
            overIndex === i && dragIndex !== null && dragIndex !== i
              ? "border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]"
              : "border-[color:var(--color-border)]"
          } ${dragIndex === i ? "opacity-40" : ""}`}
        >
          <img src={img.dataUrl} alt="" className="h-full w-full object-cover" />
          <span className="absolute left-0.5 top-0.5 rounded-xs bg-black/60 px-1 text-[9px] text-white">
            {i + 1}
          </span>
          <button
            type="button"
            title="Remove from this import"
            aria-label={`Remove ${img.name}`}
            onClick={() => onRemove(i)}
            className="absolute right-0.5 top-0.5 hidden h-4 w-4 items-center justify-center rounded-xs bg-black/60 text-[10px] text-white group-hover:flex hover:bg-black/80"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── the view ─────────────────────────

export function ImportPhotosView({
  name,
  images: initialImages,
  failed,
  customFonts,
  build,
  renderPage,
  commit,
  onCancel,
  onCommit,
}: {
  name: string;
  images: ResolvedImportImage[];
  failed: string[];
  customFonts: CustomFont[];
  build: (args: {
    items: PhotoItem[];
    theme: PhotoThemeConfig;
    faces: MdFace[];
    images: Record<string, { dataUrl: string; width: number; height: number }>;
    meta: SheetLayoutMeta;
  }) => Promise<{ pages: PageInfo[] }>;
  renderPage: (pageIndex: number, scale: number) => Promise<RenderResult>;
  commit: () => Promise<ArrayBuffer>;
  onCancel: () => void;
  onCommit: (bytes: ArrayBuffer, pageCount: number) => void;
}) {
  const [images, setImages] = useState(initialImages);

  const [choice, setChoice] = useState<ThemeChoice>(loadStoredChoice);
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORE_KEY, JSON.stringify(choice));
    } catch {
      /* storage full/blocked — theme just won't persist */
    }
  }, [choice]);

  const config = choice.config;
  const update = useCallback((fn: (c: PhotoThemeConfig) => void) => {
    setChoice((prev) => {
      const next = clonePhotoTheme(prev.config);
      fn(next);
      return { ...prev, config: next, dirty: true };
    });
  }, []);
  const pickPreset = useCallback((id: string) => {
    const preset = PHOTO_THEMES.find((p) => p.id === id);
    if (!preset) return;
    setChoice({ presetId: id, config: clonePhotoTheme(preset.config), dirty: false });
  }, []);

  const reorder = useCallback((from: number, to: number) => {
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (moved) next.splice(to, 0, moved);
      return next;
    });
  }, []);
  const removeAt = useCallback((index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const meta = useMemo<SheetLayoutMeta>(
    () => ({
      file: name,
      date: new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    }),
    [name]
  );

  // ── build loop (debounced; generation-guarded) ──
  const [pages, setPages] = useState<PageInfo[] | null>(null);
  const [renderGen, setRenderGen] = useState(0);
  const [busy, setBusy] = useState(true);
  const [buildError, setBuildError] = useState<string | null>(null);
  const genRef = useRef(0);
  const hasBuiltRef = useRef(false);

  useEffect(() => {
    const gen = (genRef.current += 1);
    if (images.length === 0) {
      setPages(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            const faces = await resolveFaces(photoThemeFamilies(config), customFonts);
            if (genRef.current !== gen) return;
            const items: PhotoItem[] = images.map((im) => ({
              id: im.id,
              name: im.name,
              width: im.width,
              height: im.height,
            }));
            const imageMap: Record<string, { dataUrl: string; width: number; height: number }> = {};
            for (const im of images) {
              imageMap[im.id] = { dataUrl: im.dataUrl, width: im.width, height: im.height };
            }
            const res = await build({ items, theme: config, faces, images: imageMap, meta });
            if (genRef.current !== gen) return;
            hasBuiltRef.current = true;
            setPages(res.pages);
            setRenderGen(gen);
            setBuildError(null);
          } catch (err) {
            if (genRef.current === gen) {
              setBuildError(err instanceof Error ? err.message : String(err));
            }
          } finally {
            if (genRef.current === gen) setBusy(false);
          }
        })();
      },
      hasBuiltRef.current ? 250 : 0
    );
    return () => clearTimeout(timer);
  }, [images, config, customFonts, build, meta]);

  // ── preview zoom ──
  const previewRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<"fit" | number>("fit");
  const [previewW, setPreviewW] = useState(800);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPreviewW(el.clientWidth));
    ro.observe(el);
    setPreviewW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const pageW = Math.max(...(pages?.map((p) => p.width) ?? [PAGE_PRESETS.A4![0]]));
  const scale =
    zoom === "fit" ? Math.max(0.2, Math.min(1.6, (previewW - 64) / pageW)) : zoom;

  // ── commit ──
  const [committing, setCommitting] = useState(false);
  const doImport = useCallback(() => {
    if (busy || committing || !pages || pages.length === 0) return;
    setCommitting(true);
    void (async () => {
      try {
        const bytes = await commit();
        onCommit(bytes, pages.length);
      } catch (err) {
        setCommitting(false);
        setBuildError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [busy, committing, pages, commit, onCommit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const [openSection, setOpenSection] = useState<string | null>(null);
  const toggle = (id: string) => setOpenSection((s) => (s === id ? null : id));
  const mm = (pt: number) => Math.round(pt / MM_TO_PT);
  const perPage = Math.max(1, config.layout.columns * config.layout.rows);

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[color:var(--color-bg)]">
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-2">
        <i className="ri-image-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Images to PDF</h2>
        <span className="min-w-0 truncate text-xs text-[color:var(--color-fg-muted)]">{name}</span>
        <span className="text-xs text-[color:var(--color-fg-muted)]">
          {images.length === 0
            ? "no images"
            : pages === null
              ? "building…"
              : `${images.length} image${images.length === 1 ? "" : "s"} · ${pages.length} page${pages.length === 1 ? "" : "s"}`}
          {busy && pages !== null && " · updating…"}
        </span>
        {failed.length > 0 && (
          <span className="text-xs text-amber-600" title={failed.join("\n")}>
            <i className="ri-error-warning-line" aria-hidden /> {failed.length} file
            {failed.length === 1 ? "" : "s"} skipped
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={typeof zoom === "number" ? String(zoom) : "fit"}
            onChange={(e) => setZoom(e.target.value === "fit" ? "fit" : Number(e.target.value))}
            className="h-7 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs"
            title="Preview zoom"
          >
            <option value="fit">Fit width</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
          </select>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || committing || !pages || pages.length === 0}
            onClick={doImport}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            <i className="ri-file-transfer-line" aria-hidden />
            {committing ? "Importing…" : "Import as PDF"}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* sidebar */}
        <div className="flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-r border-[color:var(--color-border)] p-3">
          <div>
            <div className="mb-1.5 flex items-center">
              <p className="text-xs font-medium">Layout</p>
              {choice.dirty && (
                <button
                  type="button"
                  onClick={() => pickPreset(choice.presetId)}
                  className="ml-auto text-[11px] text-[color:var(--color-accent)] hover:underline"
                  title="Discard customisations and restore the preset"
                >
                  Reset customisations
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PHOTO_THEMES.map((preset) => (
                <ThemeCard
                  key={preset.id}
                  preset={preset}
                  active={choice.presetId === preset.id}
                  onPick={() => pickPreset(preset.id)}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium">
              Images
              <span className="ml-1 font-normal text-[color:var(--color-fg-muted)]">
                drag to reorder
              </span>
            </p>
            <ImageStrip images={images} onReorder={reorder} onRemove={removeAt} />
          </div>

          <p className="text-xs font-medium">
            Customise
            {choice.dirty && (
              <span className="ml-1 font-normal text-[color:var(--color-fg-muted)]">(edited)</span>
            )}
          </p>

          <Section title="Page" open={openSection === "page"} onToggle={() => toggle("page")}>
            <Row label="Size">
              <select
                value={config.page.preset}
                onChange={(e) => update((c) => (c.page.preset = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                {Object.keys(PAGE_PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <select
                value={config.page.landscape ? "landscape" : "portrait"}
                onChange={(e) => update((c) => (c.page.landscape = e.target.value === "landscape"))}
                disabled={perPage === 1 && config.layout.autoOrient}
                title={
                  perPage === 1 && config.layout.autoOrient
                    ? "Each page follows its image's orientation"
                    : undefined
                }
                className={`${fieldCls} flex-1 disabled:opacity-50`}
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </Row>
            <Row label="Margins mm">
              <Num
                title="Top / bottom"
                value={mm(config.page.marginTop)}
                min={0}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginTop = v * MM_TO_PT;
                    c.page.marginBottom = v * MM_TO_PT;
                  })
                }
              />
              <Num
                title="Left / right"
                value={mm(config.page.marginLeft)}
                min={0}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginLeft = v * MM_TO_PT;
                    c.page.marginRight = v * MM_TO_PT;
                  })
                }
              />
            </Row>
            <Row label="Background">
              <ColorPicker
                value={config.page.background}
                onChange={(v) => update((c) => (c.page.background = v))}
                title="Page background"
              />
            </Row>
          </Section>

          <Section title="Grid & fit" open={openSection === "grid"} onToggle={() => toggle("grid")}>
            <Row label="Per page">
              <Num
                title="Columns"
                value={config.layout.columns}
                min={1}
                max={8}
                step={1}
                onChange={(v) => update((c) => (c.layout.columns = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">×</span>
              <Num
                title="Rows"
                value={config.layout.rows}
                min={1}
                max={8}
                step={1}
                onChange={(v) => update((c) => (c.layout.rows = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">
                = {perPage}
              </span>
            </Row>
            <Row label="Fit">
              <select
                value={config.layout.fit}
                onChange={(e) => update((c) => (c.layout.fit = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                <option value="contain">Whole image (letterbox)</option>
                <option value="cover">Fill cell (crop)</option>
                <option value="shrink">Frame hugs image</option>
              </select>
            </Row>
            <Row label="Gap">
              <Num
                value={config.layout.gap}
                min={0}
                max={60}
                step={1}
                onChange={(v) => update((c) => (c.layout.gap = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
            </Row>
            {perPage === 1 && (
              <Row label="Orientation">
                <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                  <input
                    type="checkbox"
                    checked={config.layout.autoOrient}
                    onChange={(e) => update((c) => (c.layout.autoOrient = e.target.checked))}
                  />
                  Match each image
                </label>
              </Row>
            )}
          </Section>

          <Section title="Frame" open={openSection === "frame"} onToggle={() => toggle("frame")}>
            <Row label="Border">
              <Num
                value={config.frame.borderWidth}
                min={0}
                max={12}
                step={0.25}
                onChange={(v) => update((c) => (c.frame.borderWidth = v))}
                title="Border thickness, pt"
              />
              <ColorPicker
                value={config.frame.borderColor}
                onChange={(v) => update((c) => (c.frame.borderColor = v))}
                title="Border colour"
              />
            </Row>
            <Row label="Mat">
              <Num
                value={config.frame.matWidth}
                min={0}
                max={40}
                step={1}
                onChange={(v) => update((c) => (c.frame.matWidth = v))}
                title="Mat width, pt"
              />
              <ColorPicker
                value={config.frame.matColor}
                onChange={(v) => update((c) => (c.frame.matColor = v))}
                title="Mat colour"
              />
            </Row>
            <Row label="Shadow">
              <Num
                value={config.frame.shadowOffset}
                min={0}
                max={20}
                step={0.5}
                onChange={(v) => update((c) => (c.frame.shadowOffset = v))}
                title="Shadow offset, pt"
              />
              <ColorPicker
                value={config.frame.shadowColor}
                onChange={(v) => update((c) => (c.frame.shadowColor = v))}
                title="Shadow colour"
              />
            </Row>
          </Section>

          <Section title="Captions" open={openSection === "caption"} onToggle={() => toggle("caption")}>
            <Row label="Show">
              <select
                value={config.caption.source}
                onChange={(e) => update((c) => (c.caption.source = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                <option value="">No captions</option>
                <option value="filename">File name</option>
                <option value="index">Number</option>
              </select>
            </Row>
            {config.caption.source && (
              <>
                <Row label="Font">
                  <FontSelect
                    value={config.caption.family}
                    onChange={(v) => update((c) => (c.caption.family = v))}
                    customFonts={customFonts}
                  />
                </Row>
                <Row label="Size / colour">
                  <Num
                    value={config.caption.size}
                    min={5}
                    max={20}
                    onChange={(v) => update((c) => (c.caption.size = v))}
                  />
                  <ColorPicker
                    value={config.caption.color}
                    onChange={(v) => update((c) => (c.caption.color = v))}
                    title="Caption colour"
                  />
                  <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                    <input
                      type="checkbox"
                      checked={config.caption.italic}
                      onChange={(e) => update((c) => (c.caption.italic = e.target.checked))}
                    />
                    Italic
                  </label>
                </Row>
                <Row label="Align">
                  <AlignSelect
                    value={config.caption.align}
                    onChange={(v) => update((c) => (c.caption.align = v))}
                  />
                </Row>
              </>
            )}
          </Section>

          <Section
            title="Page header & footer"
            open={openSection === "pageText"}
            onToggle={() => toggle("pageText")}
          >
            <Row label="Header">
              <input
                type="text"
                value={config.pageHeader.text}
                placeholder="(none)"
                onChange={(e) => update((c) => (c.pageHeader.text = e.target.value))}
                className={`${fieldCls} flex-1`}
              />
            </Row>
            {config.pageHeader.text && (
              <Row label="">
                <AlignSelect
                  value={config.pageHeader.align}
                  onChange={(v) => update((c) => (c.pageHeader.align = v))}
                />
                <Num
                  value={config.pageHeader.size}
                  min={5}
                  max={14}
                  onChange={(v) => update((c) => (c.pageHeader.size = v))}
                  title="Size, pt"
                />
                <ColorPicker
                  value={config.pageHeader.color}
                  onChange={(v) => update((c) => (c.pageHeader.color = v))}
                  title="Header colour"
                />
              </Row>
            )}
            <Row label="Footer">
              <input
                type="text"
                value={config.pageFooter.text}
                placeholder="(none)"
                onChange={(e) => update((c) => (c.pageFooter.text = e.target.value))}
                className={`${fieldCls} flex-1`}
              />
            </Row>
            {config.pageFooter.text && (
              <Row label="">
                <AlignSelect
                  value={config.pageFooter.align}
                  onChange={(v) => update((c) => (c.pageFooter.align = v))}
                />
                <Num
                  value={config.pageFooter.size}
                  min={5}
                  max={14}
                  onChange={(v) => update((c) => (c.pageFooter.size = v))}
                  title="Size, pt"
                />
                <ColorPicker
                  value={config.pageFooter.color}
                  onChange={(v) => update((c) => (c.pageFooter.color = v))}
                  title="Footer colour"
                />
              </Row>
            )}
            <p className="text-[10px] text-[color:var(--color-fg-muted)]">
              Placeholders: {"{page} {pages} {file} {date}"}
            </p>
          </Section>

          <p className="text-[10px] text-[color:var(--color-fg-muted)]">
            JPEG and PNG files are embedded at full resolution without
            recompression; other formats (WebP, AVIF, HEIC, GIF, BMP, SVG)
            are converted first. The preview is the generated PDF itself —
            importing keeps it exactly as shown.
          </p>
        </div>

        {/* preview */}
        <div ref={previewRef} className="min-w-0 flex-1 overflow-auto bg-[color:var(--color-bg-subtle)]">
          {buildError ? (
            <div className="p-6 text-sm text-red-600">Could not build the preview: {buildError}</div>
          ) : images.length === 0 ? (
            <div className="p-6 text-sm text-[color:var(--color-fg-muted)]">
              No images left in this import — add some or cancel.
            </div>
          ) : pages === null ? (
            <div className="p-6 text-sm text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line animate-spin" aria-hidden /> Building preview…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-7 p-6 pb-10">
              {pages.map((info, i) => (
                <PreviewPage
                  key={i}
                  pageIndex={i}
                  info={info}
                  scale={scale}
                  gen={renderGen}
                  renderPage={renderPage}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
